import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { buildMenuHppMap, buildPackageMap, resolveLineHpp } from '../../../lib/hpp';
import { localDateStr } from '../../../lib/date';

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const fmtRp  = n => 'Rp ' + (n || 0).toLocaleString('id-ID');
const fmtPct = (cur, prev) => {
  if (!prev) return null;
  return Math.round((cur - prev) / prev * 100);
};

const RANGES = [
  { label: '7 Hari',  days: 7  },
  { label: '30 Hari', days: 30 },
  { label: '90 Hari', days: 90 },
];

// ── Reusable components ───────────────────────────────────────────
const S = {
  card:  { background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', padding: '18px 20px' },
  muted: { fontSize: '.76rem', color: 'var(--muted)' },
  mono:  { fontFamily: "'DM Mono',monospace" },
  serif: { fontFamily: "'Baloo 2',sans-serif" },
};

function SectionLabel({ children }) {
  return <div style={{ ...S.mono, fontSize: '.58rem', letterSpacing: '1.8px', textTransform: 'uppercase', color: 'rgba(201,146,42,.7)', marginBottom: 10 }}>{children}</div>;
}

function CardTitle({ children }) {
  return <div style={{ ...S.serif, fontSize: '1.05rem', marginBottom: 14 }}>{children}</div>;
}

function DeltaChip({ pct, higherGood = true }) {
  if (pct === null || pct === undefined) return null;
  const good = higherGood ? pct >= 0 : pct <= 0;
  return (
    <span style={{ ...S.mono, fontSize: '.62rem', background: good ? '#E8F5E9' : '#FFEBEE', color: good ? '#1B5E20' : '#C62828', borderRadius: 20, padding: '2px 7px', marginLeft: 6, whiteSpace: 'nowrap' }}>
      {pct > 0 ? '↑' : pct < 0 ? '↓' : '→'}{Math.abs(pct)}%
    </span>
  );
}

function BarRow({ label, value, maxValue, color = 'var(--gold)', sub }) {
  const pct = maxValue > 0 ? Math.min(100, Math.round(value / maxValue * 100)) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', marginBottom: 3 }}>
        <span style={{ flex: 1, marginRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{sub}</span>
      </div>
      <div style={{ background: '#EEE', borderRadius: 50, height: 5 }}>
        <div style={{ width: pct + '%', height: '100%', borderRadius: 50, background: color, transition: 'width .6s' }} />
      </div>
    </div>
  );
}

function KPITile({ label, value, sub, delta, higherGood = true, accent = '#EFF6FF', valueColor }) {
  return (
    <div style={{ ...S.card, padding: '14px 16px' }}>
      <div style={{ fontSize: '.7rem', ...S.mono, letterSpacing: '.6px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>{label}</div>
      <div style={{ ...S.serif, fontSize: '1.4rem', color: valueColor || 'var(--dark)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: 2 }}>{sub}</div>}
      <DeltaChip pct={delta} higherGood={higherGood} />
    </div>
  );
}

function Insight({ text, level = 'info' }) {
  const map = { ok: ['#E8F5E9', '#1B5E20'], warn: ['#FFF8E1', '#E65100'], danger: ['#FFEBEE', '#C62828'], info: ['#EFF6FF', '#1C5BD4'] };
  const [bg, c] = map[level] || map.info;
  return <div style={{ background: bg, color: c, borderRadius: 8, padding: '9px 14px', fontSize: '.8rem', lineHeight: 1.6, marginBottom: 8 }}>{text}</div>;
}

// ─────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────
export default function Analytics() {
  const [range, setRange]   = useState(30);
  const [d, setD]           = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (days) => {
    setLoading(true);

    const now      = new Date();
    const fromDate = new Date(now); fromDate.setDate(now.getDate() - days);
    const prevTo   = new Date(fromDate); prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo);   prevFrom.setDate(prevTo.getDate() - days + 1);

    const fromStr     = fromDate.toISOString();
    const prevFromStr = prevFrom.toISOString();
    const prevToStr   = prevTo.toISOString();

    const [
      posRes, poRes,
      prevPosRes, prevPoRes,
      custRes,
      posOrdersAllRes,
      heatmapRes,
      referralRes,
      menuItemsRes,
      packagesRes,
    ] = await Promise.all([
      // POS orders periode ini — order_items ditarik lewat relasi FK sekalian
      // (JANGAN query tabel order_items terpisah dengan filter created_at:
      // tabel order_items TIDAK punya kolom created_at, query begitu akan
      // selalu gagal/null dan bikin Margin & Menu Terlaris kosong terus)
      supabase.from('pos_orders')
        .select('total, status, payment, created_at, loyalty_discount, birthday_discount, loyalty_points_used, order_items(display_name,qty,unit_price,hpp_snapshot)')
        .gte('created_at', fromStr),

      // PO orders periode ini
      supabase.from('po_orders')
        .select('qty, total, status, payment_method, payment_status, created_at, po_batches(product:products(name,price))')
        .gte('created_at', fromStr).neq('status', 'batal'),

      // POS orders periode sebelumnya (untuk delta) — order_items sekalian
      supabase.from('pos_orders')
        .select('total, status, payment, order_items(qty,unit_price,hpp_snapshot,display_name)')
        .gte('created_at', prevFromStr).lte('created_at', prevToStr),

      // PO orders periode sebelumnya
      supabase.from('po_orders')
        .select('qty, total, status, payment_method, payment_status')
        .gte('created_at', prevFromStr).lte('created_at', prevToStr).neq('status', 'batal'),

      // Customers
      supabase.from('customers')
        .select('id, created_at, loyalty_tier, visit_count, last_seen_at'),

      // POS orders untuk daily revenue chart (maks 90 hari)
      supabase.from('pos_orders')
        .select('total, status, created_at')
        .gte('created_at', fromStr).eq('status', 'Selesai'),

      // POS orders untuk heatmap jam & hari (ambil created_at semua order selesai)
      supabase.from('pos_orders')
        .select('created_at')
        .gte('created_at', fromStr)
        .eq('status', 'Selesai'),

      // PO referral stats
      supabase.from('po_orders')
        .select('qty, total, ref_code, referred_by_user_id, created_at, users:referred_by_user_id(name, ref_code)')
        .gte('created_at', fromStr)
        .not('referred_by_user_id', 'is', null)
        .neq('status', 'batal')
        .or('payment_method.eq.cod,and(payment_method.eq.non-tunai,payment_status.eq.paid)'),

      // Menu items + paket — untuk fallback HPP live (lihat lib/hpp.js)
      supabase.from('menu_items').select('id,name,hpp,base_price'),
      supabase.from('packages').select('id,name,price,package_items(qty,menu_items(hpp,base_price))'),
    ]);

    // ── POS periode ini ───────────────────────────────────────────
    const pos      = posRes.data || [];
    const posDone  = pos.filter(o => o.status === 'Selesai');
    const posBatal = pos.filter(o => o.status === 'Batal');
    const posRev   = posDone.reduce((s, o) => s + o.total, 0);
    const posAOV   = posDone.length ? Math.round(posRev / posDone.length) : 0;
    const loyDisc  = posDone.reduce((s, o) => s + (o.loyalty_discount||0) + (o.birthday_discount||0), 0);
    const cancelPct = pos.length ? Math.round(posBatal.length / pos.length * 100) : 0;
    const payMap   = {};
    posDone.forEach(o => { payMap[o.payment] = (payMap[o.payment]||0) + 1; });

    // HPP map (untuk koreksi HPP paket & fallback HPP item lama — lihat lib/hpp.js)
    const menuHppMap = buildMenuHppMap(menuItemsRes.data || []);
    const packageMap = buildPackageMap(packagesRes.data || []);

    // ── POS margin periode ini — hanya dari order yang sudah Selesai
    // (konsisten dengan posRev di atas, yang juga cuma menjumlah Selesai) ──
    const items = posDone.flatMap(o => o.order_items || []);
    let totalHPP = 0, totalRevHPP = 0, estimatedHppCount = 0;
    items.forEach(i => {
      const { hpp, isLive } = resolveLineHpp(i, packageMap, menuHppMap);
      if (hpp > 0) {
        if (isLive) estimatedHppCount++;
        totalHPP += hpp;
        totalRevHPP += i.unit_price * i.qty;
      }
    });
    const marginAmt = totalRevHPP - totalHPP;
    const marginPct = totalRevHPP > 0 ? Math.round(marginAmt / totalRevHPP * 100) : null;

    // ── POS top menu + profitability matrix ───────────────────────
    const menuMap = {};
    items.forEach(i => {
      const name = (i.display_name || '').split(' – ')[0].trim();
      if (!name) return;
      if (!menuMap[name]) menuMap[name] = { name, qty: 0, revenue: 0, hpp: 0, hasHPP: false };
      menuMap[name].qty     += i.qty;
      menuMap[name].revenue += i.unit_price * i.qty;
      const { hpp } = resolveLineHpp(i, packageMap, menuHppMap);
      if (hpp > 0) { menuMap[name].hpp += hpp; menuMap[name].hasHPP = true; }
    });
    const menuList = Object.values(menuMap).map(m => ({
      ...m,
      marginPct: m.hasHPP && m.revenue > 0 ? Math.round((m.revenue - m.hpp) / m.revenue * 100) : null,
    })).sort((a, b) => b.qty - a.qty);
    const topMenuPOS = menuList.slice(0, 8);

    // BCG-style profitability matrix
    const medQty = menuList.length ? menuList[Math.floor(menuList.length/2)]?.qty || 0 : 0;
    const marginsOnly = menuList.filter(m => m.marginPct !== null).map(m => m.marginPct).sort((a,b)=>a-b);
    const medMargin   = marginsOnly.length ? marginsOnly[Math.floor(marginsOnly.length/2)] : 30;
    const matrix = { star: [], cashcow: [], gem: [], dog: [], nodata: [] };
    menuList.forEach(m => {
      if (m.marginPct === null) { matrix.nodata.push(m); return; }
      const hiVol = m.qty >= medQty, hiMar = m.marginPct >= medMargin;
      if (hiVol && hiMar)  matrix.star.push(m);
      else if (hiVol)       matrix.cashcow.push(m);
      else if (hiMar)       matrix.gem.push(m);
      else                  matrix.dog.push(m);
    });

    // ── PO periode ini ────────────────────────────────────────────
    const po       = poRes.data || [];
    const validPO  = po.filter(o => o.payment_method === 'cod' || (o.payment_method === 'non-tunai' && o.payment_status === 'paid'));
    const poPacks  = validPO.reduce((s, o) => s + o.qty, 0);
    const poRev    = validPO.reduce((s, o) => s + o.total, 0);
    const prevValidPO = (prevPoRes.data || []).filter(o => o.payment_method === 'cod' || (o.payment_method === 'non-tunai' && o.payment_status === 'paid'));
    const prevPacks = prevValidPO.reduce((s,o)=>s+o.qty, 0);
    const prevPoRev = prevValidPO.reduce((s,o)=>s+o.total, 0);
    const poByProd = {};
    po.forEach(o => {
      const name = o.po_batches?.product?.name || 'Lainnya';
      if (!poByProd[name]) poByProd[name] = { name, qty: 0, revenue: 0 };
      poByProd[name].qty     += o.qty;
      poByProd[name].revenue += o.qty * (o.po_batches?.product?.price || 0);
    });
    const topPO = Object.values(poByProd).sort((a,b) => b.qty - a.qty).slice(0, 5);

    // ── Periode sebelumnya (delta) ────────────────────────────────
    const prevPos     = prevPosRes.data || [];
    const prevPosDone = prevPos.filter(o => o.status === 'Selesai');
    const prevRev     = prevPosDone.reduce((s, o) => s + o.total, 0);
    const prevAOV     = prevPosDone.length ? Math.round(prevRev / prevPosDone.length) : 0;
    const prevCancel  = prevPos.length ? Math.round(prevPos.filter(o=>o.status==='Batal').length/prevPos.length*100) : 0;

    const prevItems    = prevPosDone.flatMap(o => o.order_items || []);
    let prevHPP = 0, prevRevHPP = 0;
    prevItems.forEach(i => {
      const { hpp } = resolveLineHpp(i, packageMap, menuHppMap);
      if (hpp > 0) { prevHPP += hpp; prevRevHPP += i.unit_price * i.qty; }
    });
    const prevMargin = prevRevHPP > 0 ? Math.round((prevRevHPP-prevHPP)/prevRevHPP*100) : null;

    // ── Customers ─────────────────────────────────────────────────
    const custs       = custRes.data || [];
    const newCusts    = custs.filter(c => c.created_at >= fromStr).length;
    const activeCusts = custs.filter(c => {
      const ref = c.last_seen_at || c.created_at;
      return ref && ref >= fromStr;
    }).length;
    const tierMap = custs.reduce((m,c) => { m[c.loyalty_tier]=(m[c.loyalty_tier]||0)+1; return m; }, {});

    // ── Daily revenue chart ───────────────────────────────────────
    const dailyOrders = posOrdersAllRes.data || [];
    const dailyMap = {};
    dailyOrders.forEach(o => {
      const day = o.created_at?.slice(0, 10);
      if (day) dailyMap[day] = (dailyMap[day] || 0) + o.total;
    });
    // Build array untuk range
    const dailyPoints = [];
    for (let i = days - 1; i >= 0; i--) {
      const d2 = new Date(now); d2.setDate(d2.getDate() - i);
      const key = d2.toISOString().slice(0, 10);
      dailyPoints.push({ date: key, revenue: dailyMap[key] || 0, label: d2.toLocaleDateString('id-ID', { day:'numeric', month:'short' }) });
    }

    // ── Auto insights ─────────────────────────────────────────────
    const insights = [];
    const revDelta = fmtPct(posRev, prevRev);
    if (revDelta !== null && revDelta >= 15) insights.push({ text: `✅ Revenue naik ${revDelta}% dibanding ${days} hari sebelumnya. Momentum positif — pertahankan!`, level: 'ok' });
    if (revDelta !== null && revDelta <= -15) insights.push({ text: `⚠️ Revenue turun ${Math.abs(revDelta)}% dibanding periode sebelumnya. Perlu investigasi.`, level: 'warn' });
    if (marginPct !== null && marginPct < 20) insights.push({ text: `⚠️ Margin kotor ${marginPct}% — di bawah target 30%. Evaluasi harga atau HPP menu.`, level: 'warn' });
    if (cancelPct > 10) insights.push({ text: `🔴 Cancel rate ${cancelPct}% — cukup tinggi. Cek stok dan kendala pembayaran.`, level: 'danger' });
    if (matrix.dog.length > 0) insights.push({ text: `💡 ${matrix.dog.length} menu masuk kategori "Dog" (volume rendah, margin rendah). Pertimbangkan untuk dievaluasi.`, level: 'info' });
    if (matrix.gem.length > 0) insights.push({ text: `💎 ${matrix.gem.length} menu kategori "Gem" — margin bagus tapi jarang dipesan. Promosikan lebih aktif.`, level: 'info' });

    // ── Heatmap: hari × jam ──────────────────────────────────────
    // DOW: 0=Minggu … 6=Sabtu
    // hourGrid[dow][hour] = jumlah order
    const hourGrid = Array.from({length: 7}, () => Array(24).fill(0));
    const dowTotal = Array(7).fill(0);
    const hourTotal = Array(24).fill(0);
    (heatmapRes.data || []).forEach(o => {
      const dt  = new Date(o.created_at);
      const dow = dt.getDay();
      const h   = dt.getHours();
      hourGrid[dow][h]++;
      dowTotal[dow]++;
      hourTotal[h]++;
    });
    // Nama hari (Senin=1 … Minggu=0)
    const DOW_LABELS = ['Min','Sen','Sel','Rab','Kam','Jum','Sab'];
    // Hari tersibuk
    const busiestDow  = dowTotal.indexOf(Math.max(...dowTotal));
    // Jam tersibuk
    const busiestHour = hourTotal.indexOf(Math.max(...hourTotal));
    // Peak hours: jam dengan order ≥ 70% dari jam tersibuk
    const peakThreshold = Math.max(...hourTotal) * 0.7;
    const peakHours = hourTotal.map((cnt, h) => cnt >= peakThreshold && cnt > 0 ? h : -1).filter(h => h >= 0);

    // ── Referral stats ───────────────────────────────────────────
    const refMap = {};
    (referralRes.data || []).forEach(o => {
      const uid  = o.referred_by_user_id;
      const name = o.users?.name || o.ref_code || uid;
      if (!refMap[uid]) refMap[uid] = { name, ref_code: o.users?.ref_code || o.ref_code, packs: 0, orders: 0, revenue: 0 };
      refMap[uid].packs   += o.qty;
      refMap[uid].orders  += 1;
      refMap[uid].revenue += o.total;
    });
    const referralStats = Object.values(refMap).sort((a, b) => b.packs - a.packs);

    setD({
      pos, posDone, posBatal, posRev, posAOV, loyDisc, cancelPct, payMap,
      marginAmt, marginPct, estimatedHppCount,
      topMenuPOS, matrix,
      po, poPacks, poRev, topPO,
      prevRev, prevPacks, prevPoRev,
      deltaRev:    fmtPct(posRev,   prevRev),
      deltaOrders: fmtPct(pos.length, prevPos.length),
      deltaAOV:    fmtPct(posAOV,   prevAOV),
      deltaCancelPct: prevCancel !== null ? cancelPct - prevCancel : null,
      deltaMargin: fmtPct(marginPct, prevMargin),
      deltaPacks:  fmtPct(poPacks,  prevPacks),
      deltaPoRev:  fmtPct(poRev,    prevPoRev),
      custs: custs.length, newCusts, activeCusts, tierMap,
      dailyPoints,
      insights,
      hourGrid, dowTotal, hourTotal, DOW_LABELS, busiestDow, busiestHour, peakHours,
      referralStats,
    });
    setLoading(false);
  }, []);

  useEffect(() => { load(range); }, [range, load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: '.84rem' }}>Memuat data analytics...</div>;
  if (!d) return null;

  const maxDailyRev = Math.max(...d.dailyPoints.map(p => p.revenue), 1);
  const totalPayments = Object.values(d.payMap).reduce((s,v)=>s+v,0) || 1;

  // Tentukan berapa label tanggal yang ditampilkan di chart
  const step = d.dailyPoints.length <= 14 ? 1 : d.dailyPoints.length <= 30 ? 3 : 7;

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>
      <style>{`
        @media(max-width:640px){
          .an-grid2{grid-template-columns:1fr!important}
          .an-grid3{grid-template-columns:1fr 1fr!important}
          .an-kpi  {grid-template-columns:1fr 1fr!important}
          .an-matrix{grid-template-columns:1fr!important}
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ ...S.serif, fontSize: '1.5rem' }}>Analytics</div>
          <div style={{ ...S.muted }}>Performa bisnis vs periode sebelumnya yang sama</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {RANGES.map(r => (
            <button key={r.days} onClick={() => setRange(r.days)}
              style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border)', background: range === r.days ? '#CD5226' : 'white', color: range === r.days ? '#fff' : 'var(--muted)', fontSize: '.76rem', cursor: 'pointer' }}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Insights ── */}
      {d.insights.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {d.insights.map((ins, i) => <Insight key={i} text={ins.text} level={ins.level} />)}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 1. KPI Growth — POS                                       */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionLabel>Kasir (POS) — vs {range} hari sebelumnya</SectionLabel>
      <div className="an-kpi" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
        <KPITile label="Revenue" value={fmtRp(d.posRev)} delta={d.deltaRev} />
        <KPITile label="Transaksi Selesai" value={d.posDone.length} delta={d.deltaOrders} sub={`${d.posBatal.length} dibatal`} />
        <KPITile label="AOV" value={fmtRp(d.posAOV)} delta={d.deltaAOV} />
        <KPITile
          label="Margin Kotor"
          value={d.marginPct !== null ? `${d.marginPct}%` : '—'}
          sub={d.estimatedHppCount > 0 ? `${d.estimatedHppCount} item pakai HPP terkini` : undefined}
          delta={d.deltaMargin}
          valueColor={d.marginPct !== null ? (d.marginPct >= 30 ? '#1B5E20' : d.marginPct >= 15 ? '#E65100' : '#C62828') : undefined}
        />
        <KPITile label="Cancel Rate" value={`${d.cancelPct}%`} delta={d.deltaCancelPct} higherGood={false}
          valueColor={d.cancelPct === 0 ? '#1B5E20' : d.cancelPct > 10 ? '#C62828' : '#E65100'} />
        <KPITile label="Loyalty Discount" value={fmtRp(d.loyDisc)} delta={null} />
        <KPITile label="Total Order" value={d.pos.length} delta={d.deltaOrders} />
        <KPITile label="Margin Nominal" value={fmtRp(d.marginAmt)} delta={null}
          valueColor={d.marginAmt >= 0 ? '#1B5E20' : '#C62828'} />
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 2. Daily Revenue Chart                                    */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionLabel>Tren Revenue Harian</SectionLabel>
      <div style={{ ...S.card, marginBottom: 20 }}>
        <CardTitle>Revenue Harian {range} Hari Terakhir</CardTitle>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80 }}>
          {d.dailyPoints.map((pt, i) => {
            const h = maxDailyRev > 0 ? Math.max(3, Math.round(pt.revenue / maxDailyRev * 80)) : 3;
            const isToday = pt.date === localDateStr();
            return (
              <div key={pt.date} title={`${pt.label}: ${fmtRp(pt.revenue)}`}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <div style={{ width: '80%', height: h, background: isToday ? 'var(--dark)' : pt.revenue > 0 ? 'var(--gold)' : '#EEE', borderRadius: '3px 3px 0 0', transition: 'height .4s' }} />
              </div>
            );
          })}
        </div>
        {/* X-axis labels — hanya setiap N hari */}
        <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
          {d.dailyPoints.map((pt, i) => (
            <div key={pt.date} style={{ flex: 1, textAlign: 'center', fontSize: '.58rem', color: 'var(--muted)', overflow: 'hidden' }}>
              {i % step === 0 ? pt.label : ''}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, ...S.muted }}>
          <span>Total: <strong style={{ color: 'var(--dark)' }}>{fmtRp(d.posRev)}</strong></span>
          <span>Rata-rata/hari: <strong style={{ color: 'var(--dark)' }}>{fmtRp(Math.round(d.posRev / range))}</strong></span>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 3. Menu + Payment                                         */}
      {/* ══════════════════════════════════════════════════════════ */}
      <div className="an-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        {/* Top menu POS */}
        <div style={S.card}>
          <CardTitle>Menu POS Terlaris</CardTitle>
          {d.topMenuPOS.length === 0
            ? <div style={S.muted}>Belum ada data</div>
            : d.topMenuPOS.map((m, i) => {
              const COLORS = ['#1C5BD4','#1B5E20','#E65100','#7C3AED','#BE185D','#0891B2','#D97706','#65A30D'];
              return (
                <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ ...S.mono, fontSize: '.68rem', color: COLORS[i%8], minWidth: 14 }}>{i+1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', marginBottom: 2 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                      <span style={{ ...S.mono, color: COLORS[i%8], marginLeft: 8 }}>{m.qty}x</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <div style={{ flex: 1, background: '#EEE', borderRadius: 50, height: 4 }}>
                        <div style={{ width: `${Math.round(m.qty/d.topMenuPOS[0].qty*100)}%`, height: '100%', borderRadius: 50, background: COLORS[i%8], transition: 'width .5s' }} />
                      </div>
                      {m.marginPct !== null && (
                        <span style={{ fontSize: '.66rem', ...S.mono, color: m.marginPct >= 30 ? '#1B5E20' : m.marginPct >= 15 ? '#E65100' : '#C62828', whiteSpace: 'nowrap' }}>{m.marginPct}%</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>

        {/* Payment breakdown */}
        <div style={S.card}>
          <CardTitle>Metode Pembayaran POS</CardTitle>
          {Object.keys(d.payMap).length === 0
            ? <div style={S.muted}>Belum ada data</div>
            : Object.entries(d.payMap).sort((a,b)=>b[1]-a[1]).map(([pay, cnt]) => {
              const pct  = Math.round(cnt / totalPayments * 100);
              const PAYCOLORS = { Cash: '#E65100', Tunai: '#E65100', QRIS: '#1C5BD4', Transfer: '#7C3AED' };
              const c = PAYCOLORS[pay] || 'var(--gold)';
              return <BarRow key={pay} label={pay} value={cnt} maxValue={totalPayments} color={c} sub={`${cnt}x · ${pct}%`} />;
            })
          }
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
            <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginBottom: 2 }}>Total Transaksi Selesai</div>
            <div style={{ ...S.serif, fontSize: '1.2rem' }}>{d.posDone.length}x · {fmtRp(d.posRev)}</div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 4. Profitability Matrix (BCG-style)                       */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionLabel>Menu Profitability Matrix</SectionLabel>
      <div style={{ ...S.card, marginBottom: 20 }}>
        <CardTitle>Klasifikasi Menu: Volume vs Margin</CardTitle>
        <div style={{ fontSize: '.76rem', color: 'var(--muted)', marginBottom: 14 }}>
          Threshold — volume: median {d.topMenuPOS[0] ? `≥${Math.round(d.topMenuPOS[Math.floor(d.topMenuPOS.length/2)]?.qty||0)}x` : ''} · margin: median
        </div>
        {d.topMenuPOS.filter(m => m.marginPct === null).length > 0 && (
          <div style={{ background: '#EFF6FF', borderRadius: 6, padding: '6px 12px', fontSize: '.76rem', color: '#1C5BD4', marginBottom: 12 }}>
            ℹ️ {d.topMenuPOS.filter(m=>m.marginPct===null).length} menu tidak tampil karena HPP belum diisi
          </div>
        )}
        <div className="an-matrix" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { key: 'star',    icon: '⭐', label: 'Star',     desc: 'Volume ↑  Margin ↑', tip: 'Produk unggulan — pertahankan',        bg: '#FFFBEB', border: '#FCD34D', titleC: '#92400E' },
            { key: 'cashcow', icon: '💰', label: 'Cash Cow', desc: 'Volume ↑  Margin ↓', tip: 'Terjual banyak, margin tipis',          bg: '#ECFDF5', border: '#6EE7B7', titleC: '#065F46' },
            { key: 'gem',     icon: '💎', label: 'Gem',      desc: 'Volume ↓  Margin ↑', tip: 'Margin bagus, kurang dikenal — promosi', bg: '#F5F3FF', border: '#C4B5FD', titleC: '#5B21B6' },
            { key: 'dog',     icon: '⚠️', label: 'Dog',      desc: 'Volume ↓  Margin ↓', tip: 'Tidak menguntungkan — evaluasi',        bg: '#FFF1F2', border: '#FCA5A5', titleC: '#991B1B' },
          ].map(q => (
            <div key={q.key} style={{ background: q.bg, border: `1px solid ${q.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: '.88rem', color: q.titleC }}>{q.icon} {q.label}</span>
                <span style={{ ...S.mono, fontSize: '.62rem', color: q.titleC, background: 'rgba(0,0,0,.06)', borderRadius: 20, padding: '2px 7px' }}>{d.matrix[q.key].length} menu</span>
              </div>
              <div style={{ fontSize: '.72rem', color: q.titleC, opacity: .8, marginBottom: 6 }}>{q.desc}</div>
              <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginBottom: 8, fontStyle: 'italic' }}>{q.tip}</div>
              {d.matrix[q.key].length === 0
                ? <div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>Tidak ada menu</div>
                : d.matrix[q.key].slice(0, 4).map(m => (
                  <div key={m.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', padding: '3px 0', borderBottom: '1px solid rgba(0,0,0,.06)' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8 }}>{m.name}</span>
                    <span style={{ ...S.mono, fontSize: '.68rem', color: q.titleC, whiteSpace: 'nowrap' }}>
                      {m.qty}x {m.marginPct !== null ? `· ${m.marginPct}%` : ''}
                    </span>
                  </div>
                ))
              }
              {d.matrix[q.key].length > 4 && <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginTop: 4 }}>+ {d.matrix[q.key].length-4} lainnya</div>}
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 4b. Heatmap Jam & Hari                                    */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionLabel>Pola Waktu Transaksi</SectionLabel>
      <div className="an-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        {/* Heatmap hari × jam */}
        <div style={{ ...S.card, gridColumn: '1 / -1' }}>
          <CardTitle>Heatmap Transaksi — Hari × Jam</CardTitle>
          <div style={{ fontSize: '.76rem', color: 'var(--muted)', marginBottom: 14 }}>
            Warna makin gelap = makin banyak order. Hari tersibuk: <strong>{d.DOW_LABELS[d.busiestDow]}</strong> · Jam tersibuk: <strong>{String(d.busiestHour).padStart(2,'0')}:00</strong>
          </div>
          {/* Grid: 7 baris (hari) × 24 kolom (jam) */}
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 520 }}>
              {/* Jam header */}
              <div style={{ display: 'grid', gridTemplateColumns: '32px repeat(24, 1fr)', gap: 2, marginBottom: 3 }}>
                <div />
                {Array.from({length:24},(_,h) => (
                  <div key={h} style={{ textAlign:'center', fontSize:'.52rem', color:'var(--muted)', fontFamily:"'DM Mono',monospace" }}>
                    {h % 3 === 0 ? String(h).padStart(2,'0') : ''}
                  </div>
                ))}
              </div>
              {/* Rows per hari */}
              {[1,2,3,4,5,6,0].map(dow => {
                const maxCell = Math.max(...d.hourGrid.flat(), 1);
                return (
                  <div key={dow} style={{ display:'grid', gridTemplateColumns:'32px repeat(24,1fr)', gap:2, marginBottom:2 }}>
                    <div style={{ fontSize:'.66rem', color: dow === d.busiestDow ? 'var(--dark)' : 'var(--muted)', fontFamily:"'DM Mono',monospace", display:'flex', alignItems:'center', fontWeight: dow === d.busiestDow ? 700 : 400 }}>
                      {d.DOW_LABELS[dow]}
                    </div>
                    {d.hourGrid[dow].map((cnt, h) => {
                      const intensity = cnt / maxCell;
                      const bg = cnt === 0 ? '#F0ECE4'
                        : intensity < 0.25 ? '#FDE68A'
                        : intensity < 0.5  ? '#F59E0B'
                        : intensity < 0.75 ? '#D97706'
                        : '#92400E';
                      const isPeak = h === d.busiestHour && dow === d.busiestDow;
                      return (
                        <div key={h} title={`${d.DOW_LABELS[dow]} ${String(h).padStart(2,'0')}:00 — ${cnt} order`}
                          style={{ height:18, borderRadius:2, background:bg, outline: isPeak ? '2px solid var(--dark)' : 'none', cursor:'default', transition:'background .3s' }} />
                      );
                    })}
                  </div>
                );
              })}
              {/* Legend */}
              <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:10, fontSize:'.68rem', color:'var(--muted)' }}>
                <span>Sepi</span>
                {['#F0ECE4','#FDE68A','#F59E0B','#D97706','#92400E'].map(c => (
                  <div key={c} style={{ width:16, height:10, borderRadius:2, background:c }} />
                ))}
                <span>Ramai</span>
              </div>
            </div>
          </div>
        </div>

        {/* Hari paling sibuk */}
        <div style={S.card}>
          <CardTitle>Order per Hari dalam Seminggu</CardTitle>
          <div style={{ fontSize:'.76rem', color:'var(--muted)', marginBottom:12 }}>
            Akumulasi {range} hari terakhir
          </div>
          {[1,2,3,4,5,6,0].map(dow => {
            const cnt = d.dowTotal[dow];
            const maxDow = Math.max(...d.dowTotal, 1);
            const isBest = dow === d.busiestDow;
            return (
              <div key={dow} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <div style={{ width:28, fontSize:'.72rem', fontFamily:"'DM Mono',monospace", color: isBest ? 'var(--dark)' : 'var(--muted)', fontWeight: isBest ? 700 : 400 }}>
                  {d.DOW_LABELS[dow]}
                </div>
                <div style={{ flex:1, background:'#EEE', borderRadius:50, height:6 }}>
                  <div style={{ width: cnt > 0 ? `${Math.round(cnt/maxDow*100)}%` : '0%', height:'100%', borderRadius:50, background: isBest ? 'var(--dark)' : 'var(--gold)', transition:'width .6s' }} />
                </div>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'.74rem', color: isBest ? 'var(--dark)' : 'var(--muted)', fontWeight: isBest ? 700 : 400, minWidth:28, textAlign:'right' }}>
                  {cnt}
                </div>
                {isBest && <span style={{ fontSize:'.6rem', background:'#CD5226', color:'#fff', borderRadius:20, padding:'1px 7px', fontFamily:"'DM Mono',monospace" }}>peak</span>}
              </div>
            );
          })}
        </div>

        {/* Jam paling sibuk */}
        <div style={S.card}>
          <CardTitle>Jam Tersibuk (Peak Hours)</CardTitle>
          <div style={{ fontSize:'.76rem', color:'var(--muted)', marginBottom:12 }}>
            Akumulasi {range} hari terakhir · jam ≥ 70% dari peak ditandai
          </div>
          <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:64, marginBottom:6 }}>
            {d.hourTotal.map((cnt, h) => {
              const maxH = Math.max(...d.hourTotal, 1);
              const barH = cnt > 0 ? Math.max(3, Math.round(cnt/maxH*64)) : 2;
              const isPeak = d.peakHours.includes(h);
              const isBest = h === d.busiestHour;
              return (
                <div key={h} title={`${String(h).padStart(2,'0')}:00 — ${cnt} order`}
                  style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center' }}>
                  <div style={{ width:'85%', height:barH, background: isBest ? 'var(--dark)' : isPeak ? 'var(--gold)' : cnt > 0 ? '#C9922A44' : '#EEE', borderRadius:'3px 3px 0 0', transition:'height .4s', outline: isBest ? '2px solid var(--gold)' : 'none' }} />
                </div>
              );
            })}
          </div>
          <div style={{ display:'flex', gap:2, marginBottom:10 }}>
            {d.hourTotal.map((_, h) => (
              <div key={h} style={{ flex:1, textAlign:'center', fontSize:'.5rem', color:'var(--muted)', fontFamily:"'DM Mono',monospace" }}>
                {h % 3 === 0 ? String(h).padStart(2,'0') : ''}
              </div>
            ))}
          </div>
          {d.peakHours.length > 0 && (
            <div style={{ background:'#FFFDE7', borderRadius:8, padding:'8px 12px', fontSize:'.78rem' }}>
              🕐 <strong>Peak hours:</strong> {d.peakHours.map(h => `${String(h).padStart(2,'0')}:00`).join(', ')}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 5. PO Stats                                               */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionLabel>Pre-Order (PO) — vs {range} hari sebelumnya</SectionLabel>
      <div className="an-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div style={{ ...S.card }}>
          <CardTitle>Statistik PO</CardTitle>
          {[
            ['Total Order PO', d.po.length, null],
            ['Pack Terjual', d.poPacks.toLocaleString('id-ID') + ' pack', d.deltaPacks],
            ['Revenue PO', fmtRp(d.poRev), d.deltaPoRev],
          ].map(([label, val, delta]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F5EEE6' }}>
              <span style={S.muted}>{label}</span>
              <div>
                <strong style={{ fontSize: '.88rem' }}>{val}</strong>
                <DeltaChip pct={delta} />
              </div>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <CardTitle>Produk PO Terlaris</CardTitle>
          {d.topPO.length === 0
            ? <div style={S.muted}>Belum ada data</div>
            : d.topPO.map((p, i) => (
              <BarRow key={p.name} label={p.name} value={p.qty} maxValue={d.topPO[0].qty}
                color={['#1C5BD4','#1B5E20','#E65100','#7C3AED','#BE185D'][i]}
                sub={`${p.qty} pack · ${fmtRp(p.revenue)}`} />
            ))
          }
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 6. Customers                                              */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionLabel>Customer</SectionLabel>
      <div className="an-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        <div style={S.card}>
          <CardTitle>Ringkasan Customer</CardTitle>
          {[
            ['Total Customer', d.custs],
            ['Customer Baru', `${d.newCusts} (${range} hari)`],
            [`Aktif (${range} hari)`, d.activeCusts],
          ].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F5EEE6', fontSize: '.84rem' }}>
              <span style={S.muted}>{l}</span><strong>{v}</strong>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <CardTitle>Distribusi Tier Loyalty</CardTitle>
          {['Kenalan', 'Langganan', 'Sahabat', 'Keluarga'].map((tier, i) => {
            const n   = d.tierMap[tier] || 0;
            const pct = d.custs ? Math.round(n / d.custs * 100) : 0;
            const TIERCOLORS = ['#9CA3AF', '#1C5BD4', '#7C3AED', '#E65100'];
            return (
              <BarRow key={tier} label={tier} value={n} maxValue={d.custs} color={TIERCOLORS[i]}
                sub={`${n} (${pct}%)`} />
            );
          })}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 7. Referral Staf                                          */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionLabel>Referral Staf — PO</SectionLabel>
      <div style={{ ...S.card, marginBottom: 20 }}>
        <CardTitle>Performa Referral per Staf</CardTitle>
        <div style={{ fontSize: '.76rem', color: 'var(--muted)', marginBottom: 14 }}>
          Order PO yang masuk via link <span style={{ fontFamily: "'DM Mono',monospace" }}>?ref=kode</span> · {range} hari terakhir
        </div>
        {d.referralStats.length === 0 ? (
          <div style={{ ...S.muted, padding: '12px 0' }}>
            Belum ada order via link referral dalam periode ini. Pastikan staf sudah punya kode referral di Master → Pengguna POS.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
            <thead>
              <tr style={{ background: '#F8F5F0' }}>
                {['Staf', 'Kode', 'Order', 'Pack', 'Revenue', 'Est. Bonus'].map(h => (
                  <th key={h} style={{ padding: '7px 12px', textAlign: h === 'Staf' || h === 'Kode' ? 'left' : 'right', ...S.mono, fontSize: '.58rem', letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.referralStats.map((r, i) => {
                const estBonus = r.packs * 500;
                const maxPacks = d.referralStats[0]?.packs || 1;
                return (
                  <tr key={r.ref_code || i}
                    onMouseEnter={e => e.currentTarget.style.background = '#FDFAF6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #F5EEE6', fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          {r.name}
                          <div style={{ height: 4, borderRadius: 50, background: '#EEE', marginTop: 4, width: 80 }}>
                            <div style={{ width: `${Math.round(r.packs/maxPacks*100)}%`, height: '100%', borderRadius: 50, background: 'var(--gold)', transition: 'width .5s' }} />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #F5EEE6' }}>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.72rem', background: '#E3F2FD', color: '#0D47A1', borderRadius: 6, padding: '2px 8px' }}>{r.ref_code || '—'}</span>
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono }}>{r.orders}</td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono, fontWeight: 600 }}>{r.packs} pack</td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono }}>{'Rp ' + r.revenue.toLocaleString('id-ID')}</td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #F5EEE6', textAlign: 'right', ...S.mono, fontWeight: 600, color: '#1B5E20' }}>{'Rp ' + estBonus.toLocaleString('id-ID')}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: '#E8F5E9' }}>
                <td colSpan={3} style={{ padding: '8px 12px', ...S.mono, fontSize: '.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px' }}>Total</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', ...S.mono, fontWeight: 700 }}>{d.referralStats.reduce((s,r) => s+r.packs, 0)} pack</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', ...S.mono, fontWeight: 700 }}>{'Rp ' + d.referralStats.reduce((s,r) => s+r.revenue, 0).toLocaleString('id-ID')}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', ...S.mono, fontWeight: 700, color: '#1B5E20' }}>{'Rp ' + d.referralStats.reduce((s,r) => s+r.packs*500, 0).toLocaleString('id-ID')}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

    </div>
  );
}
